import React, { useCallback, useEffect, useState } from 'react';
import { authedFetch, isAdmin } from './utils/api';
import { color, naira, radius, space } from './ui/theme';
import { Banner, Btn, Card, Chip } from './ui/kit';

/**
 * Every payroll that has been calculated, and what still has to happen to it.
 *
 * A run moves DRAFT -> APPROVED -> PAID, and each step means something
 * different in the accounts:
 *
 *   DRAFT      calculated only. Nobody is paid, nothing is in the ledger.
 *   APPROVED   the employment cost is recognised and the company now owes it.
 *   PAID       the money has left, and the liability is settled.
 *
 * Approving and paying are separate deliberately. Approving says the figures
 * are right; paying says the money has actually gone. Collapsing them would
 * mean the ledger showed cash leaving on the day somebody checked a
 * spreadsheet.
 */

const TONE = {
  DRAFT: 'warning', APPROVED: 'info', PAID: 'success', CANCELLED: 'neutral',
};

const WHAT_IT_MEANS = {
  DRAFT: 'Calculated. Nobody is paid and nothing has reached the accounts.',
  APPROVED: 'The cost is in the ledger and the company owes this. Not yet paid.',
  PAID: 'The money has gone and the liability is settled.',
  CANCELLED: 'Voided. Its period is free to be processed again.',
};

const inputStyle = {
  width: '100%', padding: '8px 10px', borderRadius: radius.sm,
  border: `1px solid ${color.border}`, fontSize: 13,
};

async function req(url, opts) {
  const res = await authedFetch(url, opts);
  if (!res.ok) {
    let d = `Request failed (${res.status})`;
    try { const j = await res.json(); d = j.detail || j.message || d; } catch { /* keep */ }
    throw new Error(typeof d === 'string' ? d : 'Request failed');
  }
  return res.json();
}

const theMonth = (a, b) => {
  const s = new Date(a), e = new Date(b);
  const same = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
  return same
    ? s.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
    : `${s.toLocaleDateString('en-GB')} – ${e.toLocaleDateString('en-GB')}`;
};

export default function PayrollRuns({ notify }) {
  const [runs, setRuns] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);       // run being looked at
  const [slips, setSlips] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [paidOn, setPaidOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState('');
  const admin = isAdmin();

  const load = useCallback(async () => {
    setError(null);
    try { setRuns(await req('/api/payroll/runs')); }
    catch (e) { setError(e.message); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openRun = async (run) => {
    setOpen(run); setSlips(null); setPicked(new Set());
    try {
      const d = await req(`/api/payroll/runs/${run.id}/payslips`);
      const list = Array.isArray(d) ? d : (d.payslips || []);
      setSlips(list);
      // Everything unpaid is pre-selected: paying the whole run is the normal
      // case, and unticking two people is less work than ticking twelve.
      setPicked(new Set(list.filter((s) => !s.paid_at).map((s) => s.id)));
    } catch (e) { notify?.(e.message, 'error'); setSlips([]); }
  };

  const act = async (url, body, ok) => {
    setBusy(true);
    try {
      const r = await req(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
      });
      notify?.(r.message || r.detail || ok, 'success');
      await load();
      if (open) {
        const fresh = (await req('/api/payroll/runs')).find((x) => x.id === open.id);
        if (fresh) { setOpen(fresh); await openRun(fresh); }
      }
      return r;
    } catch (e) { notify?.(e.message, 'error'); return null; }
    finally { setBusy(false); }
  };

  const calculateThisMonth = async () => {
    const r = await act('/api/payroll/runs/auto-current-month', {},
                        'This month calculated.');
    if (r?.nothing_to_do) notify?.(r.detail, 'info');
  };

  if (error) return <Banner tone="danger" title="Could not load payroll">{error}</Banner>;

  const selectedTotal = (slips || [])
    .filter((s) => picked.has(s.id))
    .reduce((t, s) => t + Number(s.net_pay || 0), 0);

  return (
    <div>
      <Card style={{ marginBottom: space(2) }}>
        <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 15 }}>Processed payrolls</h3>
            <div style={{ fontSize: 12.5, color: color.textSecondary }}>
              Every run that has been calculated, and what still has to happen
              to it.
            </div>
          </div>
          {admin && (
            <Btn size="sm" onClick={calculateThisMonth} disabled={busy}>
              {busy ? 'Working…' : 'Calculate this month'}
            </Btn>
          )}
        </div>
      </Card>

      {runs && runs.length === 0 && (
        <Banner tone="info" title="No payroll has been run yet">
          A run is drafted automatically once a month ends. You can also
          calculate the current month now with the button above.
        </Banner>
      )}

      {(runs || []).map((r) => (
        <Card key={r.id} style={{ marginBottom: space(1.5) }}>
          <div style={{ display: 'flex', justifyContent: 'space-between',
                        alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>
                {theMonth(r.period_start, r.period_end)}
                <span style={{ fontWeight: 400, color: color.textSecondary,
                               marginLeft: 8, fontSize: 12.5 }}>
                  {r.run_number}
                </span>
              </div>
              <div style={{ fontSize: 12.5, color: color.textSecondary,
                            marginTop: 2 }}>
                {r.payslip_count} payslip{r.payslip_count === 1 ? '' : 's'}
                {r.paid_count > 0 && r.paid_count < r.payslip_count &&
                  ` · ${r.paid_count} paid so far`}
                {r.approved_by && ` · approved by ${r.approved_by}`}
              </div>
              <div style={{ fontSize: 12, color: color.textMuted, marginTop: 4 }}>
                {WHAT_IT_MEANS[r.status] || ''}
              </div>
              {r.cancellation_reason && (
                <div style={{ fontSize: 12, color: color.danger, marginTop: 4 }}>
                  Cancelled by {r.cancelled_by}: {r.cancellation_reason}
                </div>
              )}
            </div>

            <div style={{ textAlign: 'right' }}>
              <Chip tone={TONE[r.status] || 'neutral'}>{r.status}</Chip>
              <div style={{ fontSize: 17, fontWeight: 700, marginTop: 6 }}>
                {naira(r.net_total)}
              </div>
              <div style={{ fontSize: 11.5, color: color.textSecondary }}>
                net to staff
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: space(1.5),
                        flexWrap: 'wrap' }}>
            <Btn size="sm" variant="secondary" onClick={() => openRun(r)}>
              View details
            </Btn>
            {admin && r.status === 'DRAFT' && (
              <Btn size="sm" disabled={busy}
                   onClick={() => act(`/api/payroll/runs/${r.id}/approve`,
                                      { approved_by: 'Admin' },
                                      'Approved and posted to the ledger.')}>
                Approve
              </Btn>
            )}
            {admin && r.status !== 'CANCELLED' && !r.fully_paid && (
              <Btn size="sm" variant="secondary" disabled={busy}
                   onClick={() => {
                     const why = window.prompt(
                       'Why is this run being cancelled? It frees the period '
                       + 'to be processed again.');
                     if (!why || why.trim().length < 10) {
                       notify?.('A cancellation needs a reason', 'error');
                       return;
                     }
                     act(`/api/payroll/runs/${r.id}/cancel`,
                         { cancelled_by: 'Admin', reason: why.trim() },
                         'Cancelled.');
                   }}>
                Cancel
              </Btn>
            )}
          </div>
        </Card>
      ))}

      {/* ---- one run, in full -------------------------------------------- */}
      {open && (
        <div className="modal-overlay" onClick={() => setOpen(null)}>
          <div className="modal" style={{ maxWidth: 860, width: '95%' }}
               onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0 }}>
                {theMonth(open.period_start, open.period_end)} &mdash; {open.run_number}
              </h3>
              <Btn size="sm" variant="secondary" onClick={() => setOpen(null)}>
                Close
              </Btn>
            </div>

            <div className="modal-body"
                 style={{ maxHeight: '72vh', overflowY: 'auto' }}>
              <div style={{ display: 'grid', gap: 10, marginBottom: space(2),
                            gridTemplateColumns:
                              'repeat(auto-fit, minmax(150px, 1fr))' }}>
                <Fact label="Gross" value={naira(open.gross_total)} />
                <Fact label="Deductions" value={naira(open.deductions_total)} />
                <Fact label="Net to staff" value={naira(open.net_total)} strong />
                <Fact label="Employer cost" value={naira(open.employer_cost_total)} />
              </div>

              {slips === null && (
                <div style={{ color: color.textSecondary, fontSize: 13 }}>
                  Loading payslips&hellip;
                </div>
              )}

              {slips && (
                <>
                  <table style={{ width: '100%', borderCollapse: 'collapse',
                                  fontSize: 13 }}>
                    <thead>
                      <tr style={{ textAlign: 'left', fontSize: 12,
                                   color: color.textSecondary }}>
                        <th style={{ padding: '6px 4px', width: 30 }}></th>
                        <th style={{ padding: '6px 4px' }}>Staff</th>
                        <th style={{ padding: '6px 4px', textAlign: 'right' }}>Gross</th>
                        <th style={{ padding: '6px 4px', textAlign: 'right' }}>Deductions</th>
                        <th style={{ padding: '6px 4px', textAlign: 'right' }}>Net</th>
                        <th style={{ padding: '6px 4px' }}>Paid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {slips.map((s) => (
                        <tr key={s.id} style={{ borderTop: `1px solid ${color.border}` }}>
                          <td style={{ padding: '6px 4px' }}>
                            {!s.paid_at && (
                              <input type="checkbox" checked={picked.has(s.id)}
                                     onChange={(e) => {
                                       const n = new Set(picked);
                                       if (e.target.checked) n.add(s.id); else n.delete(s.id);
                                       setPicked(n);
                                     }} />
                            )}
                          </td>
                          <td style={{ padding: '6px 4px' }}>
                            {s.staff?.name}
                            <span style={{ color: color.textMuted, fontSize: 11.5 }}>
                              {' '}{s.staff?.employee_id}
                            </span>
                          </td>
                          <td style={{ padding: '6px 4px', textAlign: 'right' }}>
                            {naira(s.gross_pay)}
                          </td>
                          <td style={{ padding: '6px 4px', textAlign: 'right',
                                       color: color.textSecondary }}>
                            {naira(s.total_deductions)}
                          </td>
                          <td style={{ padding: '6px 4px', textAlign: 'right',
                                       fontWeight: 600 }}>
                            {naira(s.net_pay)}
                          </td>
                          <td style={{ padding: '6px 4px' }}>
                            {s.paid_at
                              ? <Chip tone="success">Paid</Chip>
                              : <span style={{ color: color.textMuted,
                                               fontSize: 12 }}>not yet</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {admin && open.status !== 'CANCELLED' && picked.size > 0 && (
                    <div style={{ borderTop: `1px solid ${color.border}`,
                                  marginTop: space(2), paddingTop: space(2) }}>
                      <h4 style={{ margin: `0 0 ${space(1)}` }}>
                        Mark {picked.size} payslip{picked.size === 1 ? '' : 's'} as paid
                      </h4>
                      {open.status === 'DRAFT' && (
                        <Banner tone="warning" title="This run is not approved yet">
                          Approve it first. Approving is what puts the cost in
                          the ledger; paying an unapproved run would settle a
                          liability the accounts have never recognised.
                        </Banner>
                      )}
                      <div style={{ fontSize: 13, margin: `${space(1)} 0` }}>
                        Total going out: <strong>{naira(selectedTotal)}</strong>
                      </div>
                      <div style={{ display: 'grid', gap: 8, marginBottom: 10,
                                    gridTemplateColumns:
                                      'repeat(auto-fit, minmax(170px, 1fr))' }}>
                        <label>
                          <div style={{ fontSize: 11.5, color: color.textSecondary,
                                        marginBottom: 4 }}>
                            Date the money left
                          </div>
                          <input style={inputStyle} type="date" value={paidOn}
                                 onChange={(e) => setPaidOn(e.target.value)} />
                        </label>
                        <label>
                          <div style={{ fontSize: 11.5, color: color.textSecondary,
                                        marginBottom: 4 }}>
                            Bank reference
                          </div>
                          <input style={inputStyle} value={reference}
                                 placeholder="Transfer reference"
                                 onChange={(e) => setReference(e.target.value)} />
                        </label>
                      </div>
                      <Btn size="sm" disabled={busy || open.status === 'DRAFT'}
                           onClick={() => act(
                             `/api/payroll/runs/${open.id}/payments`,
                             { payslip_ids: [...picked], paid_on: paidOn,
                               method: 'BANK_TRANSFER',
                               bank_reference: reference.trim() || null },
                             'Payment recorded and posted to the ledger.')}>
                        {busy ? 'Recording…' : 'Mark as paid'}
                      </Btn>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Fact({ label, value, strong }) {
  return (
    <div style={{ background: color.bg, borderRadius: radius.sm,
                  padding: '8px 10px' }}>
      <div style={{ fontSize: 10.5, color: color.textSecondary,
                    textTransform: 'uppercase', letterSpacing: 0.3 }}>
        {label}
      </div>
      <div style={{ fontSize: strong ? 15 : 13.5, fontWeight: 600 }}>{value}</div>
    </div>
  );
}
